import type { ParkingSlot } from '@/components/feature/parking/types';
import { DESTINATION_MARKER_THEME_FALLBACK, MAP_ICON_URL } from '@/constants/icons';
import { NavigationStatus } from '@/enums/floor-guide';
import { useCalculateIconSize } from '@/hooks/floor-guide/useCalculateIconSize';
import { useFloorCategory, useMapControl, useMarker, useRouteDirection, useRouteSimulation } from '@/hooks/map';
import { useConfigStore } from '@/stores';
import type { NavigationGuideEndPoint } from '@/types/floor-guide';
import type { NearbyPoiItemWithDistanceType } from '@/types/map';
import type { RoutePoint } from '@/types/route-simulation';
import { calculateEndPointFromPoi, getCurrentPois, getPoiTitleByLanguage } from '@/utils/map-poi';
import { clearMyLocationFromMap, mapPoiToMarker } from '@/utils/marker';
import { generateGetRouteOptions, generateNavigationOptions, generateWalkingOptions } from '@/utils/route-simulation';
import { createDestinationPointIconUrl, createWalkingPersonIconUrl } from '@/utils/svg';
import type { DabeeoMap } from 'dabeeomaps/dist/src/DabeeoMap';
import type { DabeeoMapData } from 'dabeeomaps/dist/src/DabeeoMapData';
import type { IPoi } from 'dabeeomaps/dist/src/model/map/IPoi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentBuilding } from '../useCurrentBuilding';
import i18n from '@/locales';
import { AppUtils } from '@/utils/app';
import { useTranslation } from 'react-i18next';
import { useMarkerStore } from '@/stores/markerStore';
import { FOCUS_TO_OPTIONS, POI_ID_DELIMITER } from '@/constants/floor-guide';

type UseParkingNavigationParams = {
    mapData: DabeeoMapData | undefined;
    currentMap: DabeeoMap | undefined;
    server: any;
    selectedFloorName: string;
    handleChangeFloorByName: (name: string) => Promise<void>;
    handleChangeFloor: (floorId?: string) => Promise<void>;
    setSelectedFloorName: (name: string) => void;
    addMyCurrentLocation: () => Promise<void>;
    speak: (text: string) => void;
    currentMapBuildingId?: string;
    setCurrentMapBuildingId: (buildingId: string | undefined) => void;
};

export const useParkingNavigation = ({
    mapData,
    currentMap,
    server,
    selectedFloorName,
    handleChangeFloorByName,
    handleChangeFloor,
    setSelectedFloorName,
    addMyCurrentLocation,
    speak,
    currentMapBuildingId,
    setCurrentMapBuildingId,
}: UseParkingNavigationParams) => {
    const { t } = useTranslation();
    const language = useConfigStore((state) => state.config.ui.language);
    const { getRouteDirection } = useRouteDirection(mapData);
    const { drawRouteSimulation, startRouteSimulation, stopRouteSimulation, clearRouteSimulation, pauseRouteSimulation, resumeRouteSimulation } =
        useRouteSimulation(currentMap);
    const { addMarker, clearMarker } = useMarker(currentMap);
    const { calculateIconSize } = useCalculateIconSize();
    const slotMarkerIdRef = useRef<string | undefined>(undefined);
    const contrast = useConfigStore((state) => state.config?.ui?.contrast);
    const theme = useConfigStore((state) => state.config?.server?.store?.companyTheme);
    const [destinationIconUrl, setDestinationIconUrl] = useState(MAP_ICON_URL.DESTINATION_POINT_ICON);
    const buildings = useConfigStore((state) => state.config?.server?.store?.buildings);
    const { markerIds, setMarkerIds } = useMarkerStore();
    const { handleMoveTo, handleFocusTo } = useMapControl(currentMap);

    const allPoiItems = useMemo(() => {
        if (!Array.isArray(buildings) || !buildings?.length) {
            return [];
        }

        return buildings.flatMap((building) => (Array.isArray(building.poiContents) ? building.poiContents : []));
    }, [buildings]);

    const startPoint = useMemo<RoutePoint>(
        () => ({
            floorId: server?.floor?.floorCode ?? '',
            floorName: server?.floor?.name?.['ko'] ?? '',
            position: {
                x: server?.machine?.positionX ?? 0,
                y: server?.machine?.positionY ?? 0,
            },
        }),
        [server?.floor, server?.machine]
    );

    const [showPositionPopup, setShowPositionPopup] = useState(false);
    const [showNavigationGuide, setShowNavigationGuide] = useState(false);
    const [navigationStatus, setNavigationStatus] = useState<NavigationStatus>(NavigationStatus.IDLE);
    const [selectedRoute, setSelectedRoute] = useState<any>(undefined);
    const [endPoint, setEndPoint] = useState<RoutePoint | undefined>(undefined);
    const [selectedPoiRef, setSelectedPoiRef] = useState<IPoi | undefined>(undefined);
    const [nearbyPoiList, setNearbyPoiList] = useState<NearbyPoiItemWithDistanceType[]>([]);
    const [nearbySelectedPoiId, setNearbySelectedPoiId] = useState<string | undefined>(undefined);
    const [nearbyTitle, setNearbyTitle] = useState('');
    const [nearbyFloorName, setNearbyFloorName] = useState('');
    const [walkingIconUrl, setWalkingIconUrl] = useState(MAP_ICON_URL.WALKING_PERSON_ICON);

    const { currentBuilding } = useCurrentBuilding();
    const activeBuildingId = currentMapBuildingId || currentBuilding?.studioBuildingId;

    const { selectedFloor } = useFloorCategory(selectedFloorName, activeBuildingId);

    const navigationOptions = useMemo(() => {
        const routeLineColor = contrast ? theme?.route_line_color_hc : theme?.route_line_color;

        return generateNavigationOptions(
            server?.store?.mockRoutingLineWeight,
            calculateIconSize({ width: 26, height: 26 }),
            calculateIconSize({ width: 48, height: 68 }),
            routeLineColor,
            destinationIconUrl
        );
    }, [calculateIconSize, contrast, theme?.route_line_color, theme?.route_line_color_hc, destinationIconUrl, server?.store?.mockRoutingLineWeight]);
    const walkingOptions = useMemo(
        () =>
            generateWalkingOptions(
                server?.store?.mockRoutingOption,
                server?.store?.mockRoutingSpeed,
                currentMap?.context?.getMapOptions?.()?.controlOption?.zoom,
                calculateIconSize({ width: 66, height: 66 }),
                walkingIconUrl
            ),
        [server?.store?.mockRoutingOption, server?.store?.mockRoutingSpeed, currentMap, calculateIconSize, walkingIconUrl]
    );

    const navigationGuideEndPoint = useMemo<NavigationGuideEndPoint>(
        () => ({
            floorName: endPoint?.floorName ?? '',
            buildingName: '',
            poiName: getPoiTitleByLanguage(selectedPoiRef, language),
        }),
        [endPoint?.floorName, selectedPoiRef, language]
    );

    const selectedPoiFloorName = useMemo(() => {
        if (!selectedPoiRef || !mapData) return '';
        const floor = mapData?.dataFloor?.getFloors()?.find((f: any) => f.id === selectedPoiRef.floorId);
        return floor?.name?.[0]?.text ?? '';
    }, [selectedPoiRef, mapData]);

    const handleSelectSlot = useCallback(
        async (slot: ParkingSlot) => {
            speak(slot.name);

            const allPois = mapData?.dataPoi?.getPois() ?? [];
            const poi = allPois.find((p) => p.id === slot.id);
            if (!poi) return;

            setSelectedPoiRef(poi);

            if (slotMarkerIdRef.current) {
                clearMarker(slotMarkerIdRef.current);
                slotMarkerIdRef.current = undefined;
            }

            stopRouteSimulation();
            clearRouteSimulation();

            const poiContent = allPoiItems.find((item) => item.studioPoiId === poi.id);
            const targetBuildingId = poiContent?.studioBuildingId ?? currentBuilding?.studioBuildingId;
            if (targetBuildingId && targetBuildingId !== activeBuildingId) {
                setCurrentMapBuildingId(targetBuildingId);
            }
            const floor = mapData?.dataFloor?.getFloors()?.find((f: { id: string }) => f.id === poi.floorId);
            const poiFloorName =
                floor?.name?.find((item: { lang: string; text: string }) => item.lang === language)?.text ??
                floor?.name?.find((item: { lang: string; text: string }) => item.lang === 'ko')?.text ??
                floor?.name?.[0]?.text ??
                '';
            if (poiFloorName) {
                setSelectedFloorName(poiFloorName);
                setNearbyFloorName(poiFloorName);
            }
            if (poi.floorId) {
                await handleChangeFloor(poi.floorId);
            } else if (poiFloorName && poiFloorName !== selectedFloorName) {
                await handleChangeFloorByName(poiFloorName);
            }
            handleMoveTo({ position: { x: poi.position.x, y: poi.position.y }, floorId: poi.floorId, transition: true });

            const poiMarker = mapPoiToMarker(poi, calculateIconSize({ width: 35, height: 48 }), destinationIconUrl);
            if (poiMarker) {
                const markerIds = await addMarker({ marker: [poiMarker] });
                if (Array.isArray(markerIds) && markerIds.length) {
                    slotMarkerIdRef.current = markerIds[0];
                }
            }

            const nearbyPois = getCurrentPois(mapData, { floorId: poi.floorId, position: { x: poi.position.x, y: poi.position.y } });
            const nearest = nearbyPois.find((p) => p.id !== poi.id);

            const poiWithNearby: NearbyPoiItemWithDistanceType = {
                ...poi,
                nearbyPoi: nearest,
            };
            const selectedId = `${poi?.id}${POI_ID_DELIMITER}${nearest?.id}`;
            setNearbyPoiList([poiWithNearby]);
            setNearbySelectedPoiId(selectedId);
            setNearbyTitle(getPoiTitleByLanguage(poi, language));

            setShowNavigationGuide(false);
            setShowPositionPopup(true);
        },
        [
            speak,
            mapData,
            allPoiItems,
            currentBuilding?.studioBuildingId,
            activeBuildingId,
            setCurrentMapBuildingId,
            selectedFloorName,
            language,
            handleMoveTo,
            clearMarker,
            addMarker,
            calculateIconSize,
            destinationIconUrl,
            handleChangeFloor,
            handleChangeFloorByName,
            setSelectedFloorName,
        ]
    );

    const handleDirections = useCallback(async () => {
        if (!selectedPoiRef || !mapData) return;
        if (slotMarkerIdRef.current) {
            clearMarker(slotMarkerIdRef.current);
            clearMyLocationFromMap(currentMap);
            slotMarkerIdRef.current = undefined;
        }

        const calculatedEndPoint = calculateEndPointFromPoi(selectedPoiRef, mapData);
        setEndPoint(calculatedEndPoint);

        const routeDirections = await getRouteDirection(generateGetRouteOptions(startPoint, calculatedEndPoint));
        if (!routeDirections || !Object.keys(routeDirections)?.length) return;

        setSelectedRoute(routeDirections?.recommendation);
        setShowPositionPopup(false);

        await drawRouteSimulation(routeDirections?.recommendation, navigationOptions);
        startRouteSimulation(walkingOptions);
        setNavigationStatus(NavigationStatus.RUNNING);
        setShowNavigationGuide(true);
    }, [
        selectedPoiRef,
        mapData,
        startPoint,
        getRouteDirection,
        drawRouteSimulation,
        startRouteSimulation,
        navigationOptions,
        walkingOptions,
        clearMarker,
        setMarkerIds,
        markerIds,
    ]);

    const handleEnd = useCallback(async () => {
        stopRouteSimulation();
        clearRouteSimulation();
        setShowPositionPopup(false);
        setShowNavigationGuide(false);
        setNavigationStatus(NavigationStatus.IDLE);
        setSelectedPoiRef(undefined);
        setSelectedRoute(undefined);
        setEndPoint(undefined);
        setNearbyPoiList([]);
        setNearbySelectedPoiId(undefined);
        setNearbyTitle('');
        setNearbyFloorName('');

        if (slotMarkerIdRef.current) {
            clearMarker(slotMarkerIdRef.current);
            slotMarkerIdRef.current = undefined;
        }

        await handleChangeFloor(startPoint.floorId);
        setCurrentMapBuildingId(currentBuilding?.studioBuildingId);
        const startFloorName = server?.floor?.name?.ko ?? '';
        if (startFloorName) {
            setSelectedFloorName(startFloorName);
        }
        await addMyCurrentLocation();
    }, [
        stopRouteSimulation,
        clearRouteSimulation,
        handleChangeFloor,
        startPoint.floorId,
        currentBuilding?.studioBuildingId,
        server?.floor?.name,
        setSelectedFloorName,
        addMyCurrentLocation,
        clearMarker,
    ]);

    const handlePauseSimulation = useCallback(() => {
        pauseRouteSimulation();
        setNavigationStatus(NavigationStatus.PAUSED);
    }, [pauseRouteSimulation]);

    const handleResumeSimulation = useCallback(() => {
        resumeRouteSimulation();
        setNavigationStatus(NavigationStatus.RUNNING);
    }, [resumeRouteSimulation]);

    const handleWatchAgain = useCallback(() => {
        startRouteSimulation(walkingOptions);
        setNavigationStatus(NavigationStatus.RUNNING);
    }, [startRouteSimulation, walkingOptions]);

    const handleNavigationComplete = useCallback(() => {
        setNavigationStatus(NavigationStatus.DONE);
        setTimeout(() => {
            handleFocusTo(FOCUS_TO_OPTIONS);
        }, 100);
    }, [handleFocusTo]);

    const floorAriaLabel = useMemo(() => {
        const floorName = selectedFloor?.name?.[i18n.language] ?? selectedFloor?.name?.ko ?? selectedFloorName ?? '';
        return t('floor_guide.text_to_speech.floor', {
            floorName: AppUtils.getFloorTTSText(floorName),
        });
    }, [selectedFloor, selectedFloorName, t]);

    const floorDisplayText = useMemo(() => {
        return selectedFloor?.name?.[i18n.language] ?? selectedFloor?.name?.ko ?? selectedFloorName ?? '';
    }, [selectedFloor, selectedFloorName]);

    useEffect(() => {
        let cancelled = false;
        const markerColor = contrast
            ? theme?.route_marker_color_hc || DESTINATION_MARKER_THEME_FALLBACK.CONTRAST
            : theme?.route_marker_color || DESTINATION_MARKER_THEME_FALLBACK.DEFAULT;

        Promise.all([createDestinationPointIconUrl(markerColor), createWalkingPersonIconUrl(markerColor)]).then(([destinationUrl, walkingUrl]) => {
            if (cancelled) {
                return;
            }

            setDestinationIconUrl(destinationUrl);
            setWalkingIconUrl(walkingUrl);
        });

        return () => {
            cancelled = true;
        };
    }, [contrast, theme?.route_marker_color, theme?.route_marker_color_hc]);

    const handleFloorChanged = useCallback(
        (data: { buildingId?: string; name?: { lang: string; text: string }[] }) => {
            if (Array.isArray(data?.name) && data.name.length) {
                const nextFloorName =
                    data.name.find((item) => item.lang === language)?.text ??
                    data.name.find((item) => item.lang === 'ko')?.text ??
                    data.name[0]?.text ??
                    '';
                if (nextFloorName) {
                    setSelectedFloorName(nextFloorName);
                }
            }
            if (data?.buildingId) {
                setCurrentMapBuildingId(data.buildingId);
            }
        },
        [language, setSelectedFloorName, setCurrentMapBuildingId]
    );

    return {
        showPositionPopup,
        showNavigationGuide,
        navigationStatus,
        selectedPoiRef,
        selectedRoute,
        endPoint,
        startPoint,
        navigationGuideEndPoint,
        selectedPoiFloorName,
        nearbyPoiList,
        nearbySelectedPoiId,
        nearbyTitle,
        nearbyFloorName,
        floorAriaLabel,
        floorDisplayText,
        handleSelectSlot,
        handleDirections,
        handleEnd,
        handlePauseSimulation,
        handleResumeSimulation,
        handleWatchAgain,
        handleNavigationComplete,
        handleFloorChanged,
    };
};
